package one.devsbox.www.ui.apps.wheel.edit

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import compose.assets.Svg
import compose.assets.svg.ContentSaveOutline
import kotlinx.coroutines.launch
import one.devsbox.commons.nowDateTime
import one.devsbox.shared.a.App
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.devices.ApplicationType
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.ScreenContentAction
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.getString
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import kotlin.random.Random

import one.devsbox.shared.files.ALL
import one.devsbox.shared.files.ResourceType
import one.devsbox.shared.files.RotationType
import one.devsbox.www.provider.NavigationCallbackProvider
import kotlin.time.Clock

@VM
class AppWheelEVM : NavigationViewModel() {
    private lateinit var ctx: AppWheelECtx

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    var warningMessage by mutableStateOf<String?>(null)
        private set

    var warningRes by mutableStateOf<StringResource?>(null)
        private set

    var state by mutableStateOf<DataState<AppWheelECtx.Editor>>(DataState.Loading())
        private set

    private var activePickKey: String? = null

    var scrollPosition = 0

    fun setup(ctx: AppWheelECtx) {
        this.ctx = ctx
        if (state is DataState.Loading) {
            loadApp(ctx.args.id)
        } else {
            val key = activePickKey
            if (key != null) {
                ctx.navCallback.collector.get<NavigationCallbackProvider.Task.Media>(key) { task ->
                    task.response?.also { mediaInfo ->
                        val editor = state.entry ?: return@also
                        when {
                            key == "wheel:${ctx.args.id}:splash" -> {
                                editor.themeBgStartup.set(mediaInfo.id)
                            }

                            key == "wheel:${ctx.args.id}:bg" -> {
                                editor.themeBgApp.set(mediaInfo.id)
                            }

                            key.contains(":sector:") && key.endsWith(":icon") -> {
                                val sectorId =
                                    key.substringAfter(":sector:").substringBefore(":icon")
                                        .toLongOrNull()
                                if (sectorId != null) {
                                    val item =
                                        editor.items.state.firstOrNull { it.data.idGroup == sectorId }
                                    item?.iconWebp?.set(mediaInfo.id)
                                }
                            }

                            key.contains(":sector:") && key.endsWith(":reward") -> {
                                val sectorId =
                                    key.substringAfter(":sector:").substringBefore(":reward")
                                        .toLongOrNull()
                                if (sectorId != null) {
                                    val item =
                                        editor.items.state.firstOrNull { it.data.idGroup == sectorId }
                                    item?.rewardVisual?.set(mediaInfo.id)
                                }
                            }
                        }
                    }
                }
                activePickKey = null
            }
        }
    }

    private fun loadApp(id: Long) {
        state = DataState.Loading()
        scope.launch {
            try {
                val editor = if (id > 0) {
                    val appInstance = Repository.ads.app.instance.get(id)
                    AppWheelECtx.Editor(appInstance)
                } else {
                    val appInstance = SharedIO.AppInstance(
                        id = -1,
                        name = "NEW ${Clock.nowDateTime(true)}",
                        app = SharedIO.AppInstance.App(
                            id = ctx.args.parentId,
                            type = ApplicationType.WWW_WHEEL.mask
                        ),
                        data = App.Wheel(
                            appId = ctx.args.parentId,
                            type = 40,
                            content = App.Wheel.Content()
                        )
                    )
                    AppWheelECtx.Editor(appInstance)
                }
                state = DataState.Result(editor)
                setupActions()
            } catch (it: Throwable) {
                val fallbackError = try {
                    getString(Res.string.ui_error_unknown)
                } catch (e: Throwable) {
                    "Unknown error"
                }
                state = DataState.Error(it.message ?: fallbackError)
                contentActions = emptyList()
            }
        }
    }

    private fun setupActions() {
        contentActions = buildList {
            if (ctx.me.permission.application.write) {
                add(
                    ScreenContentAction(
                        text = Res.string.ui_users_edit_btn_save,
                        color = ctx.colorPrimaryContainer,
                        icon = Svg.ContentSaveOutline
                    ) {
                        action.save()
                    }
                )
            }
        }
    }

    interface Action {
        fun save()
        fun clearWarning()
        fun addSector()
        fun deleteSector(item: AppWheelECtx.Editor.ItemEditor)
        fun back()
        fun reload()
        fun pickSplash()
        fun pickBackgroundApp()
        fun pickSectorIcon(sector: AppWheelECtx.Editor.ItemEditor)
        fun pickSectorReward(sector: AppWheelECtx.Editor.ItemEditor)
    }

    val action = object : Action {
        override fun back() {
            ctx.nav.up()
        }

        override fun reload() {
            loadApp(ctx.args.id)
        }

        override fun save() {
            val currentState = state.entry ?: return
            val trimmedName = currentState.appInstance.name.trim()
            if (trimmedName.isEmpty()) {
                warningMessage = null
                warningRes = Res.string.ui_app_instance_edit_warn_name_empty
                return
            }
            if (currentState.items.state.isEmpty()) {
                warningMessage = null
                warningRes = Res.string.ui_app_instance_edit_warn_min_sectors
                return
            }
            state = DataState.Loading(currentState)
            scope.launch {
                try {
                    val appInstance = currentState.appInstance
                    if (appInstance.id > 0) {
                        Repository.ads.app.instance.change(ctx.args.id, appInstance)
                    } else {
                        Repository.ads.app.instance.add(appInstance)
                    }
                    ctx.nav.up()
                } catch (it: Throwable) {
                    val fallbackError = try {
                        getString(Res.string.ui_error_unknown)
                    } catch (e: Throwable) {
                        "Unknown error"
                    }
                    warningRes = null
                    warningMessage = it.message ?: fallbackError
                    state = DataState.Result(currentState)
                }
            }
        }

        override fun clearWarning() {
            warningMessage = null
            warningRes = null
        }

        override fun addSector() {
            val currentState = state.entry ?: return
            val newId =
                (currentState.items.state.maxByOrNull { it.data.idGroup }?.data?.idGroup ?: 1) + 1L
            scope.launch {
                val defaultName = try {
                    getString(Res.string.ui_app_instance_edit_new_item_template, newId)
                } catch (e: Throwable) {
                    "New item $newId"
                }
                val newSector = App.Wheel.Item(
                    idGroup = newId,
                    idItem = newId,
                    name = defaultName,
                    count = 100,
                    position = currentState.items.state.size
                )
                currentState.items.add(newSector)
            }
        }

        override fun deleteSector(item: AppWheelECtx.Editor.ItemEditor) {
            val currentState = state.entry ?: return
            currentState.items.removeAll { it.key == item.key }
        }

        override fun pickSplash() {
            activePickKey = "wheel:${ctx.args.id}:splash"


            ctx.navCallback.collector.pickMedia(
                nav = ctx.nav,
                key = activePickKey!!,
                type = ResourceType.IMAGE.mask,
                orientation = if (App.Wheel.Theme.ORIENTATION_LAND == (state.entry?.orientation?.state
                        ?: App.Wheel.Theme.ORIENTATION_LAND)
                ) RotationType.HORIZONTAL.mask
                else RotationType.VERTICAL.mask
            )
        }

        override fun pickBackgroundApp() {
            activePickKey = "wheel:${ctx.args.id}:bg"
            ctx.navCallback.collector.pickMedia(
                nav = ctx.nav,
                key = activePickKey!!,
                type = ResourceType.IMAGE.mask,
                orientation = if (App.Wheel.Theme.ORIENTATION_LAND == (state.entry?.orientation?.state
                        ?: App.Wheel.Theme.ORIENTATION_LAND)
                ) RotationType.HORIZONTAL.mask
                else RotationType.VERTICAL.mask
            )
        }

        override fun pickSectorIcon(sector: AppWheelECtx.Editor.ItemEditor) {
            activePickKey = "wheel:${ctx.args.id}:sector:${sector.data.idGroup}:icon"
            ctx.navCallback.collector.pickMedia(
                nav = ctx.nav,
                key = activePickKey!!,
                type = ResourceType.IMAGE.mask,
                orientation = RotationType.ALL
            )
        }

        override fun pickSectorReward(sector: AppWheelECtx.Editor.ItemEditor) {
            activePickKey = "wheel:${ctx.args.id}:sector:${sector.data.idGroup}:reward"
            ctx.navCallback.collector.pickMedia(
                nav = ctx.nav,
                key = activePickKey!!,
                type = ResourceType.IMAGE.mask,
                orientation = if (App.Wheel.Theme.ORIENTATION_LAND == (state.entry?.orientation?.state
                        ?: App.Wheel.Theme.ORIENTATION_LAND)
                ) RotationType.HORIZONTAL.mask
                else RotationType.VERTICAL.mask
            )
        }
    }

    override fun onRelease() {}
}
